import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import is from 'is_js';
import { List } from 'immutable';
import { connect, ApiResponseHelper, Icon, Store, Tooltip } from 'omni-common-ui';
import PropTypes from 'prop-types';

import styles from './style.postcss';

import { getUnitNumberById, getUnitList } from 'data/Units/accessors';
import { actions as Session } from 'data/SessionPlans';
import { actions as GroupMembers } from 'data/GroupMembers';
import { actions as readerActions } from 'data/Reader';
import { actions as Group } from 'containers/Group';

import { ProgressBarTd } from 'components/TableView';
import CourseViewTable from 'components/CourseViewTable';

const NO_READER_RESULT = '-';

export class Reader extends Component {
  constructor(props) {
    super(props);
    this._setUpData(props);
  }

  componentWillMount() {
    const { groupId, reader } = this.props;
    if (ApiResponseHelper.shouldFetch(reader)) {
      this.props.fetchReaderIfNeeded(groupId);
    }
  }

  componentWillReceiveProps(props) {
    this._setUpData(props);
  }

  _setUpData(props) {
    const { sessions, reader } = props;
    if (! sessions || ! sessions.data || ! reader || ! reader.assignedReaderResults) {
      this._sessions = [];
      return;
    }

    this._setSessions(props);
    this._setHighlightedColumnsNo(props);
  }

  _setSessions({ sessions, reader }) {
    this._sessions = reader.assignedReaderResults
      .filter((readerResultBySession) =>
        sessions.data.some((session) => session.id === readerResultBySession.sessionId)
      )
      .map((readerResultBySession) =>
        sessions.data
          .find((session) => readerResultBySession.sessionId === session.id)
          .set('lessonNumber', readerResultBySession.lessonNumber)
          .set('unitOrderingSequence', readerResultBySession.unitOrderingSequence)
      )
      .sort(this._sortSessions);
  }

  _sortSessions(a, b) {
    if (a.index !== b.index) return a.index - b.index;
    if (a.unitOrderingSequence !== b.unitOrderingSequence) {
      return a.unitOrderingSequence - b.unitOrderingSequence;
    }
    return a.lessonNumber - b.lessonNumber;
  }

  _setHighlightedColumnsNo({ defaultSessionId }) {
    const firstIndexOfHighlightedColumn =
      this._sessions.findIndex((session) => session.id === defaultSessionId);
    const highlightedColumnSize =
      this._sessions.filter((session) => session.id === defaultSessionId).size;
    this._highlightedColumns = [];
    for (let i = 0; i < highlightedColumnSize; i++) {
      this._highlightedColumns[i] = firstIndexOfHighlightedColumn + i;
    }
  }

  _renderSessionNumbers() {
    const cellItems = [];
    cellItems.push(
      this._sessions.map((session) => (
        <CourseViewTable.Cell className={styles.Reader_sessionNumberAndDate}
            key={`${session.id}-${session.unitOrderingSequence}-${session.lessonNumber}`}
            small>
          <span>
            {`S${session.index}, U${session.unitOrderingSequence}, L${session.lessonNumber}`}
          </span>
        </CourseViewTable.Cell>
      ))
    );

    return cellItems;
  }

  _renderDates() {
    const cellItems = [];
    const { timeZone } = this.props;
    cellItems.push(
      this._sessions.map((session) => (
        <CourseViewTable.Cell className={styles.Reader_sessionNumberAndDate}
            key={`${session.id}-${session.unitOrderingSequence}-${session.lessonNumber}`}
            columnId={{ session, column: 'completion' }}
            small>
          {session.period.start
            .clone()
            .tz(timeZone)
            .format('ddd D MMM')}
        </CourseViewTable.Cell>
      ))
    );

    return cellItems;
  }

  _getHighlightedColumns() {
    const { units, defaultUnits } = this.props;

    if (is.undefined(units)) {
      return [0];
    }

    const unitIds = units.valueSeq().toArray().map((u) => u.id);

    return is.undefined(defaultUnits) ? [0] : defaultUnits.map((u) => unitIds.indexOf(u));
  }

  _renderUnitColumns(units) {
    const cellItems = [];

    if (is.undefined(units) || units.size === 0) {
      return cellItems;
    }

    cellItems.push(
      units.valueSeq().toArray().map((unit) =>
        <CourseViewTable.Cell key={`${unit.id}`}
            className={styles.Reader_sessionNumberAndDate}
            columnId={unit.id}
            small>
          {unit.name}
        </CourseViewTable.Cell>)
    );

    return cellItems;
  }

  _renderCompleteCell() {
    const completedTooltip = 'The student read the Book of the Week or' +
      ' the Book of the Unit and completed all the activities for it.';

    return <Tooltip text={completedTooltip} overlayClassName={styles.Reader_cell_tooltip}>
      <div className={styles.Reader_cell}>
        <Icon id="success" className={styles.Reader_successIcon} />
      </div>
    </Tooltip>;
  }

  _renderInCompleteCell(progress) {
    const incompletedTooltip = progress > 0 ?
      'The student has made progress but has not completed all activities' +
        ' for the Book of the Week or the Book of the Unit.' :
      'The student has opened the page but has not done any reading activities yet.';

    return <Tooltip text={incompletedTooltip} overlayClassName={styles.Reader_cell_tooltip}>
      <div className={styles.Reader_cell}>
        <div className={styles.Reader_cell_border}>
          <ProgressBarTd isHideText
              className={styles.Reader_progressBarTd}
              percent={Math.round(progress)} />
        </div>
      </div>
    </Tooltip>;
  }

  _renderNoDataCell() {
    const noDataTooltip = 'There is no data for this student.';

    return <Tooltip text={noDataTooltip} overlayClassName={styles.Reader_cell_tooltip}>
      <div className={styles.Reader_cell}>
        <div className={styles.Reader_cell_border}>{NO_READER_RESULT}</div>
      </div>
    </Tooltip>;
  }

  render() {
    const { groupId, members, reader, units } = this.props;

    if (! reader || ! reader.assignedReaderResults) {
      return null;
    }
    const state = Store.get().getState();
    const isSSCourseTypeGroup = Group.isSSCourseTypeGroup(state, groupId);
    const isShowAssignedReader = Group.hasContentPath(state, groupId) &&
      (isSSCourseTypeGroup || Group.isHFCourseTypeGroup(state, groupId));

    if (isSSCourseTypeGroup) {
      // The SS assigned reader is by unit.
      return <div>
        <CourseViewTable students={members}
            extraInfo={reader.studentUnassignedReaderResults}
            highlightedColumns={this._getHighlightedColumns()}>
          <CourseViewTable.Header>
            <CourseViewTable.Row>{this._renderUnitColumns(units)}</CourseViewTable.Row>
          </CourseViewTable.Header>
          <CourseViewTable.Body>
            {(member, column) => {
              const studentId = member.studentGeneralInfo.id;
              const studentAssignedReaderResults = [];
              (reader.assignedReaderResults
                .filter((item) => item.unitId === column) || [])
                .forEach((item) => {
                  const studentAssignedReaderResult = item.studentAssignedReaderResults
                    .find((assignedReaderResult) =>
                      assignedReaderResult.studentId === studentId &&
                      is.number(assignedReaderResult.completion)
                    );

                  if (is.object(studentAssignedReaderResult)) {
                    studentAssignedReaderResults.push(studentAssignedReaderResult.completion);
                  }
                });

              const readerCell =
                (is.array(studentAssignedReaderResults) &&
                  studentAssignedReaderResults.length > 0) ?
                  (studentAssignedReaderResults.reduce(
                    (accumulator, currentValue) =>
                      accumulator + currentValue) /
                      studentAssignedReaderResults.length) :
                  undefined;

              if (isShowAssignedReader && readerCell && is.number(readerCell)) {
                if (readerCell >= 100) {
                  return this._renderCompleteCell();
                }

                return this._renderInCompleteCell(Math.round(readerCell));
              }

              return this._renderNoDataCell();
            }}
          </CourseViewTable.Body>
        </CourseViewTable>
      </div>;
    }

    return <div>
      <CourseViewTable students={members}
          extraInfo={reader.studentUnassignedReaderResults}
          highlightedColumns={this._highlightedColumns}>
        <CourseViewTable.Header>
          <CourseViewTable.Row>{this._renderSessionNumbers()}</CourseViewTable.Row>
          <CourseViewTable.Row>{this._renderDates()}</CourseViewTable.Row>
        </CourseViewTable.Header>
        <CourseViewTable.Body>
          {(member, { session }) => {
            const studentId = member.studentGeneralInfo.id;
            const readerCell = reader.assignedReaderResults
              .find(
                (readerBySession) =>
                  readerBySession.sessionId === session.id &&
                  readerBySession.lessonNumber === session.lessonNumber
              )
              .studentAssignedReaderResults.find(
                (readerByStudent) => readerByStudent.studentId === studentId
              );

            if (isShowAssignedReader && readerCell && is.number(readerCell.completion)) {
              if (readerCell.completion >= 100) {
                return this._renderCompleteCell();
              }

              return this._renderInCompleteCell(Math.round(readerCell.completion));
            }

            return this._renderNoDataCell();
          }}
        </CourseViewTable.Body>
      </CourseViewTable>
    </div>;
  }
}

Reader.propTypes = {
  timeZone: PropTypes.string,
  sessions: PropTypes.object,
  defaultSessionId: PropTypes.string,
  members: PropTypes.object,
  groupId: PropTypes.string.isRequired,
  reader: PropTypes.object,
  fetchReaderIfNeeded: PropTypes.func,
  units: PropTypes.object,
  defaultUnits: PropTypes.object,
};

export function mapStateToProps(state, ownProps) {
  const { groupId } = ownProps.params;
  const group = state.get('rootReducer').get('groups').get(groupId).data;
  const { timeZone } = Group.getCentre(state, groupId);
  const units = getUnitList(state, groupId);
  const defaultUnits = Session.getDefaultUnits(state, groupId);
  const sessions = getSessions(group, state);
  const members = GroupMembers.getMembersByGroup(state, groupId);
  const defaultSession = Session.getDefaultSession(state, groupId);
  let reader = state.get('rootReducer').get('reader') &&
    state.get('rootReducer').get('reader').data;
  const assignedReaderData = reader && reader.assignedReaderResults &&
    reader.assignedReaderResults.map((assignedReader) =>
      assignedReader.set('unitOrderingSequence', getUnitNumberById(units, assignedReader.unitId))
    );
  reader = reader && reader.set('assignedReaderResults', assignedReaderData);

  return {
    groupId,
    members,
    sessions,
    reader,
    timeZone,
    defaultSessionId: defaultSession.id,
    units,
    defaultUnits,
  };
}

function getSessions(group, state) {
  if (is.undefined(group.sessionPlans.data)) {
    return group.sessionPlans;
  }

  const getSession = (sessionId) =>
    state
      .get('rootReducer')
      .get('sessionPlans')
      .get(sessionId);
  return group.sessionPlans
    .update('data', (data) => new List(data))
    .update('data', (data) => data.map(getSession));
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    bindActionCreators(readerActions, dispatch)
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Reader);



// WEBPACK FOOTER //
// ./containers/Group/Homework/Reader/component.jsx